import { lazy, memo, Suspense } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { hasSurfacePermission } from "../auth/access-policy";
import { useAuth } from "../auth/auth-context";
import { PageHeaderActivityProvider } from "../components/shared-ui";
import { RouteLoadingPanel } from "../components/workspace-startup";
import { adminSurfaceRoutes } from "./admin-routes";

const surfaces = adminSurfaceRoutes
  .filter(({ handle }) => handle.persistent)
  .map(({ path, handle }) => {
    const LazyPage = lazy(handle.loadPage);
    const Page = memo(function CachedWorkspaceSurface() {
      const Resolved = handle.getPage?.();
      return Resolved ? (
        <Resolved />
      ) : (
        <Suspense fallback={<RouteLoadingPanel label={handle.label} />}>
          <LazyPage />
        </Suspense>
      );
    });
    return { path, ...handle, Page };
  });

export function WorkspaceOutlet({ activePath }: { activePath: string }) {
  const { actor } = useAuth();
  const { pathname } = useLocation();
  const fullPage =
    /^\/(experiences|users|profile-reviews|itinerary-reviews|video-verifications|cyra-suggestions|support-requests|city-reports|urgent-notices|review-sessions|social-groups|social-reviews|social-message-reviews|account-deletions|email-changes|administrators)\/[^/]+$/u.test(
      pathname,
    );
  return (
    <>
      {surfaces
        .filter((surface) => hasSurfacePermission(actor, surface))
        .map(({ label, Page, path }) => (
          <section
            aria-label={`${label} workspace`}
            hidden={fullPage || activePath !== path}
            key={path}
          >
            <PageHeaderActivityProvider active={!fullPage && activePath === path}>
              <Page />
            </PageHeaderActivityProvider>
          </section>
        ))}
      <Outlet />
    </>
  );
}
